/* aa-card.js — the product card's behaviour (PLAN.md I1.12)
 *
 * Shared by every surface that renders `snippets/aa-product-card.liquid`: the product page's rows,
 * the home page's product rows and reviews, and the collection grid. Lifted out of aa-product.js so
 * a page that only draws cards does not load the product page bundle (M-243).
 *
 *   1. .aa-qty              quantity stepper (the card's, and the product page's buy row)
 *   2. addToCart, [data-aa-quick-add]   the card's add button; addToCart is also the bundle's
 *   3. [data-aa-carousel-nav]           desktop arrows for any horizontal scroller
 *
 * IDEMPOTENCE. Several sections load this file and each `<script src>` executes, so every binder
 * marks what it has wired with `data-aa-bound` and skips it next time. The binders are exposed on
 * `window.AACard` so content injected later (the recommendation row, load-more) can be wired
 * against its own subtree. `window.AAProduct` keeps the same names for the product page's callers.
 */
window.AACard = window.AACard || {};
window.AAProduct = window.AAProduct || {};

(function () {
  'use strict';

  /* ==========================================================================
     1. Quantity stepper
     ========================================================================== */

  function stepperFor(root) {
    const input = root.querySelector('.aa-qty__input');
    if (!input) return null;
    // Binding the same stepper twice would move it two at a time.
    if (root.dataset.aaBound === 'true') return null;
    root.dataset.aaBound = 'true';
    const min = Number(input.min || 1);
    const max = input.max ? Number(input.max) : Infinity;

    function clamp(value) {
      const n = Number.isFinite(value) ? value : min;
      return Math.min(max, Math.max(min, Math.round(n)));
    }

    function set(value, notify) {
      const next = clamp(value);
      if (String(next) === input.value) return next;
      input.value = String(next);
      if (notify !== false) input.dispatchEvent(new Event('change', { bubbles: true }));
      return next;
    }

    root.querySelectorAll('.aa-qty__step').forEach((button) => {
      button.addEventListener('click', () => {
        const delta = button.name === 'plus' ? 1 : -1;
        set(Number(input.value) + delta);
      });
    });

    // Typing is clamped on blur rather than on input, so a partly typed "1" on the way to "12"
    // is not snapped back to the minimum mid-keystroke.
    input.addEventListener('blur', () => set(Number(input.value)));

    return { input: input, set: set };
  }

  function wireSteppers(root) {
    (root || document).querySelectorAll('.aa-qty').forEach(stepperFor);
  }

  window.AACard.wireSteppers = wireSteppers;
  window.AAProduct.wireSteppers = wireSteppers;
  wireSteppers(document);

})();

/* ==========================================================================
   2. Cart writes — the per-card quick add, and addToCart for every other add button
   ==========================================================================
   Both post to /cart/add.js and then publish Dawn's `cart-update`, which is what makes the header
   bubble, the cart drawer and the free-shipping bar all catch up. Skipping that publish is how a
   custom add-to-cart ends up showing a stale count. */
(function () {
  'use strict';

  function addToCart(items, button) {
    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    }

    // `window.routes` is emitted by layout/theme.liquid from Liquid's own `routes` object, which is
    // the only reliable source for this URL. Building it from Shopify.routes.root gives
    // "/pl/cart/add.js" on a store whose default locale is not pl, and that 404s.
    const endpoint = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ items: items }),
    })
      .then((response) => response.json())
      .then((result) => {
        if (result.status) throw new Error(result.description || result.message);
        if (typeof window.publish === 'function') {
          window.publish('cart-update', { source: 'aa-product' });
        }
        // Showing the drawer is the confirmation. `aa-cart.js` owns both steps: `refresh` re-renders
        // it from Liquid so the new line, the total and the free-shipping progress are all correct
        // before it is seen, then `open` shows it.
        //
        // This is what D18's notification bar replaces — when that design lands, the drawer should
        // stop opening here and the bar should appear instead. Until then, removing it would leave
        // adding to cart with no feedback beyond the header count.
        if (window.AACart) {
          Promise.resolve(window.AACart.refresh()).then(() => window.AACart.open());
        }
        return result;
      })
      .finally(() => {
        if (button) {
          button.removeAttribute('aria-busy');
          button.disabled = false;
        }
      });
  }

  function wireQuickAdd(root) {
    (root || document).querySelectorAll('[data-aa-quick-add]').forEach((button) => {
      if (button.dataset.aaBound === 'true') return;
      button.dataset.aaBound = 'true';
      button.addEventListener('click', () => {
        const card = button.closest('.aa-card');
        const input = card && card.querySelector('.aa-qty__input');
        const quantity = Math.max(1, Number(input && input.value) || 1);
        addToCart([{ id: Number(button.dataset.variantId), quantity: quantity }], button);
      });
    });
  }

  window.AACard.addToCart = addToCart;
  window.AACard.wireQuickAdd = wireQuickAdd;
  window.AAProduct.wireQuickAdd = wireQuickAdd;
  wireQuickAdd(document);
})();

/* ==========================================================================
   3. Carousel arrows
   ==========================================================================
   Desktop only — see snippets/aa-carousel-nav.liquid for why. One binder serves the gallery, the
   recommendation row and the similar-products row, because all three are the same thing: an
   element with `overflow-x: auto`. The buttons scroll by one viewport of the track rather than by
   a card width, so they do not need to know what is inside it.

   The buttons are `aria-hidden` and `tabindex="-1"` on purpose: they duplicate scrolling, which
   the keyboard and the scrollbar already do, so exposing them adds two stops to the tab order that
   lead nowhere new. */
(function () {
  'use strict';

  function wireCarousels(root) {
    (root || document).querySelectorAll('[data-aa-carousel-nav]').forEach((nav) => {
      if (nav.dataset.aaBound === 'true') return;
      nav.dataset.aaBound = 'true';

      const track = document.getElementById(nav.dataset.target);
      if (!track) return;

      const prev = nav.querySelector('[data-aa-scroll="prev"]');
      const next = nav.querySelector('[data-aa-scroll="next"]');

      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        // 1px of slack: fractional card widths mean scrollLeft rarely lands exactly on the end.
        nav.hidden = max <= 1;
        if (prev) prev.disabled = track.scrollLeft <= 1;
        if (next) next.disabled = track.scrollLeft >= max - 1;
      };

      const step = (dir) => {
        track.scrollBy({ left: dir * track.clientWidth * 0.9, behavior: 'smooth' });
      };

      if (prev) prev.addEventListener('click', () => step(-1));
      if (next) next.addEventListener('click', () => step(1));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(update).observe(track);
      update();
    });
  }

  // The recommendation row is fetched and injected after first paint, so its arrows do not exist
  // when this runs. Exposed for the injector to call, exactly as the quick-add binder is.
  window.AACard.wireCarousels = wireCarousels;
  window.AAProduct.wireCarousels = wireCarousels;
  wireCarousels(document);
})();
